import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BasicResponse } from 'src/app/core/models/basic-response.model';
import { secureStorage } from 'src/app/core/secure.config';
import { removeFromLocal, getConnection, getCurrentUser, getFromLocal, hideSpinner, setOnLocal, showSpinner, getApiKey, getEndpoint } from 'src/app/core/services/functions.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { MenuService, ModuloMenu } from 'src/app/core/services/menu.service';
import { RequestsService } from 'src/app/core/services/requests.service';
import { environment } from 'src/environments/environment';
import { AuthByUsernameDto, UsuarioAuth } from '../models/auth.models';


/**
 * AuthService
 * @author Victorguz <victorguzber@gmail.com> June-2021
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private helpers: HelpersService,
    private menuService: MenuService,
    private jwtHelper: JwtHelperService,
    private requestsService: RequestsService,
    private router: Router) { }

  /**
   * Check if the user is authenticated
   * @returns true or false - if the user is authenticated
   */
  public isAuthenticated(): boolean {
    const token = this.getToken()
    return token ? !this.jwtHelper.isTokenExpired(token) : false;
  }

  /**
   * Verify user by username and password
   * @param data json with username and password
   * @returns user
   */
  async verifyWithUsernameAndPassword(data: AuthByUsernameDto): Promise<BasicResponse> {
    const response = await this.requestsService.makeRequest(environment.domainBasics.replace("{{c_emp}}", getConnection()), environment.urls.auth.login, {
      api_key: getApiKey(), ...data
    })
    if (response.success) {
      if (response.data?.must_change_password) {
        setOnLocal('cp_token', response.data.token)
        setOnLocal('cp_user', data.username)
        return new BasicResponse(true, 'must_change_password', { must_change_password: true })
      }
      if (response.success && response.data.token) {
        const token = this.jwtHelper.decodeToken(response.data.token)
        if (token.jamaradmin_key == environment.jamaradmin_key) {
          this.saveToken(response.data.token)
          this.saveUser(token.data)
        } else {
          return new BasicResponse(false, "El token obtenido es corrupto");
        }
      }
    } else if (response.message == "Usuario sin clave nueva" || response.error == "Este usuario aún no tiene una clave para esta version.") {
      return await this.verifyWithOldPassword(data)
    }
    return response
  }

  async changePassword(newPassword: string): Promise<BasicResponse> {
    const tempToken = getFromLocal('cp_token')
    const domain = environment.domainChangePassword
    return await this.requestsService.postLikeJSON(
      `${domain}${environment.urls.auth.changePassword.value}`,
      { api_key: getApiKey(), new_password: newPassword },
      { Authorization: `Bearer ${tempToken}` }
    )
  }

  clearChangePasswordData(): void {
    removeFromLocal('cp_token')
    removeFromLocal('cp_user')
  }

  /**
   * Verifica si el usuario utiliza la misma clave que en el viejo jamaradmin y pone la misma en caso de que no tenga.
   * @param data 
   */
  async verifyWithOldPassword(data: AuthByUsernameDto): Promise<BasicResponse> {
    const resultWL = await this.requestsService.postLikeFormUrlEncoded(getEndpoint("usuarioSifc_login"), {
      api_key: getApiKey(),
      c_emp: getConnection(),
      usuario_aplicativo: data.username,
      clave_aplicativo: data.password
    })
    if (resultWL.success) {
      const resultReset = await this.requestsService.makeRequest(environment.domainBasics.replace("{{c_emp}}", getConnection()), environment.urls.auth.reset, {
        api_key: getApiKey(),
        c_emp: getConnection(),
        username: data.username,
        password: data.password,
        usuario_modificacion: "JALOGIN"
      })
      return resultReset
    } else {
      return new BasicResponse(resultWL.success, resultWL.message, undefined, resultWL)
    }
  }

  async register(usuario: UsuarioAuth): Promise<BasicResponse> {
    const response = await this.requestsService.makeRequest(environment.domainBasics.replace("{{c_emp}}", getConnection()), environment.urls.auth.register, {
      c_emp: getConnection(), api_key: getApiKey(), usuario_creacion: getCurrentUser().usuario_aplicativo, ...usuario
    })
    return response
  }

  async modify(usuario: UsuarioAuth): Promise<BasicResponse> {
    const response = await this.requestsService.makeRequest(environment.domainBasics.replace("{{c_emp}}", getConnection()), environment.urls.auth.modify, {
      c_emp: getConnection(), api_key: getApiKey(), usuario_modificacion: getCurrentUser().usuario_aplicativo, ...usuario
    })
    return response
  }

  private saveUser(user: any) {
    setOnLocal(environment.COOKIE_USER, new UsuarioAuth(user))
  }

  private getToken() {
    return getFromLocal(environment.COOKIE_TOKEN)
  }

  private saveToken(token: string) {
    setOnLocal(environment.COOKIE_TOKEN, token)
  }

  /**
   * Cierra la sesión del usuario y borra su caché almacenado
   */
  public logOut() {
    showSpinner()
    localStorage.clear()
    setOnLocal(environment.LOCAL_CONNECTION, 'JA')
    this.router.navigate(["/auth/login"])
    setTimeout(() => {
      hideSpinner()
      location.reload()
    }, 100);
  }
}

